import { useState } from 'react';
import type { RentalManagerRenterContactTenantDto } from '@zg-rentals/ts-rental-manager-api-client';
import { ProofOfInsuranceStatusType } from '@zg-rentals/ts-rental-manager-api-client';
import type { TenantInfoDto } from '@zg-rentals/ts-rins-client';
import { Box, Icon, Tag, Text, TextButton } from '@zillow/constellation-10';
import { IconFileOutline } from '@zillow/constellation-icons';
import { ContactTenantModal } from '@zg-rentals/renters-insurance-c10';
import { useSelector } from '../../../selectors/useSelector';

const CONTENT = {
  TITLE: 'Proof of insurance',
  REQUESTED_STATUS: 'Requested',
  UPLOADED_STATUS: 'Uploaded',
  VIEW: 'View',
  REQUEST: 'Request',
} as const;

type ProofOfInsuranceProps = {
  tenant: RentalManagerRenterContactTenantDto;
  onRequestClick: () => void;
  onSendRequestClick: () => void;
  onSuccess?: (message: string) => void | null;
  onError?: (message: string) => void | null;
};

const ProofOfInsurance = ({
  tenant,
  onRequestClick,
  onSendRequestClick,
  onSuccess,
  onError,
}: ProofOfInsuranceProps) => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const { listingAlias } = useSelector((state) => ({
    listingAlias: state.properties.currentProperty.listingAlias,
  }));

  // Transform tenant data to match ContactTenantModal expectations
  const transformTenant = (tenant: RentalManagerRenterContactTenantDto): TenantInfoDto => ({
    email: tenant.emailAddress || '',
    firstName: tenant.firstName || '',
    renterId: tenant.renterId,
    zuid: '',
  });

  const handleRequestClick = () => {
    setIsContactModalOpen(true);
    onRequestClick();
  };

  const handleModalClose = () => {
    setIsContactModalOpen(false);
  };

  const renderStatusContent = () => {
    if (tenant.proofOfInsuranceStatus === ProofOfInsuranceStatusType.NOT_REQUESTED) {
      return (
        <TextButton tone="brand" textStyle="body-sm-bold" onClick={handleRequestClick}>
          {CONTENT.REQUEST}
        </TextButton>
      );
    }

    if (tenant.proofOfInsuranceStatus === ProofOfInsuranceStatusType.REQUESTED) {
      return (
        <Tag size="sm" tone="gray">
          {CONTENT.REQUESTED_STATUS}
        </Tag>
      );
    }

    if (tenant.proofOfInsuranceStatus === ProofOfInsuranceStatusType.SHARED) {
      return (
        <>
          <Tag size="sm" tone="gray">
            {CONTENT.UPLOADED_STATUS}
          </Tag>
          <TextButton tone="brand" textStyle="body-sm-bold">
            {CONTENT.VIEW}
          </TextButton>
        </>
      );
    }
  };

  return (
    <>
      <Box
        data-testid="InfoRowPOI"
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '8px 16px',
          border: '1px solid {colors.border.muted-alt}',
          borderRadius: 12,
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box css={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Icon role="img" size="sm">
            <IconFileOutline />
          </Icon>

          <Text textStyle="body-sm-bold">{CONTENT.TITLE}</Text>
        </Box>
        <Box css={{ display: 'flex', alignItems: 'center', gap: 16 }}>{renderStatusContent()}</Box>
      </Box>

      <ContactTenantModal
        listingAlias={listingAlias}
        isOpen={isContactModalOpen}
        tenant={transformTenant(tenant)}
        onClose={handleModalClose}
        onSendRequestClick={onSendRequestClick}
        onSuccess={onSuccess}
        onError={onError}
      />
    </>
  );
};

export default ProofOfInsurance;
